import React, { useEffect, useState } from 'react';
import NeverOverpayNav from './NeverOverpayNav';
import { neveroverpayApi } from './neveroverpayApi';
import { workflowLabel } from './neveroverpayCopy';
import './neveroverpay.css';

const EMPTY = {
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: 'BilledTwice AI',
  smtp_use_tls: true,
  outbound_email_enabled: false,
  inbound_secret: '',
  inbound_domain: '',
  google_client_id: '',
  google_client_secret: '',
  google_redirect_uri: 'http://localhost:3000/api/auth/google/callback',
  gmail_sync_enabled: false,
  retention_days: 30,
  public_app_url: 'http://localhost:3000',
  operator_emails: '',
  autonomous_enabled: false,
  autonomous_auto_approve: true,
  autonomous_send: true,
  autonomous_verify: true,
  autonomous_interval_seconds: 120,
  autonomous_max_cents: 50000,
  ai_enabled: true,
  plaid_client_id: '',
  plaid_secret: '',
  plaid_redirect_uri: 'http://localhost:3000/plaid/oauth',
  plaid_webhook_url: '',
  plaid_country_codes: '',
};

function flags(setup = {}) {
  return [
    ['AI reading emails', setup.ai_enabled],
    ['Doing it for you', setup.autonomous],
    ['Email sending ready', setup.smtp_ready],
    ['Sending is on', setup.outbound_email],
    ['Gmail login ready', setup.gmail_oauth_ready],
    ['Gmail checking is on', setup.gmail_sync_enabled],
    ['Bank match (Plaid) ready', setup.plaid_ready],
    ['Forwarding address ready', setup.inbound_forwarding],
    [`People who can change settings (${setup.operators || 0})`, (setup.operators || 0) > 0],
  ];
}

export default function NeverOverpayAdmin() {
  const [access, setAccess] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [secretHints, setSecretHints] = useState({});
  const [setup, setSetup] = useState({});
  const [health, setHealth] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function loadAll() {
    const acc = await neveroverpayApi.adminAccess();
    setAccess(acc);
    setSetup(acc.setup || {});
    if (!acc.is_admin) return;
    const [settings, healthRow, queue] = await Promise.all([
      neveroverpayApi.adminSettings(),
      neveroverpayApi.adminHealth(),
      neveroverpayApi.opsQueue(),
    ]);
    const s = settings.settings || {};
    setSetup(settings.setup || acc.setup || {});
    setSecretHints({
      smtp_password: s.smtp_password_configured,
      google_client_secret: s.google_client_secret_configured,
      inbound_secret: s.inbound_secret_configured,
      plaid_secret: s.plaid_secret_configured,
    });
    setForm({
      ...EMPTY,
      ...s,
      smtp_password: '',
      google_client_secret: '',
      inbound_secret: '',
      plaid_secret: '',
      operator_emails: Array.isArray(s.operator_emails) ? s.operator_emails.join(', ') : (s.operator_emails || ''),
    });
    setHealth(healthRow);
    setPlaybooks(queue.playbooks || []);
  }

  useEffect(() => {
    loadAll().catch((err) => setError(err.message || 'Could not load admin'));
  }, []);

  async function run(fn, ok) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      if (ok) setNotice(ok);
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const patch = { ...form };
    if (!patch.smtp_password) delete patch.smtp_password;
    if (!patch.google_client_secret) delete patch.google_client_secret;
    if (!patch.inbound_secret) delete patch.inbound_secret;
    if (!patch.plaid_secret) delete patch.plaid_secret;
    patch.smtp_port = Number(patch.smtp_port || 587);
    patch.retention_days = Number(patch.retention_days || 30);
    await neveroverpayApi.saveAdminSettings(patch);
  }

  if (!access) {
    return (
      <div className="np-root">
        <div className="np-wrap np-muted" style={{ padding: 40 }}>Loading admin…</div>
      </div>
    );
  }

  if (!access.is_admin) {
    return (
      <div className="np-root" data-testid="neveroverpay-admin">
        <div className="np-wrap">
          <NeverOverpayNav mark="Settings" />
          {error ? <div className="np-error" style={{ marginBottom: 16 }}>{error}</div> : null}
          <div className="np-card" style={{ maxWidth: 640 }}>
            <h3>Set up the product</h3>
            <p className="np-muted">
              Email sending, Gmail, forwarding, and who can change settings live here.
              {access.can_bootstrap
                ? ' No one is set up yet. Make this account the first person who can change settings.'
                : ' Ask someone who already has access to add your email under “Who can change settings.”'}
            </p>
            {access.can_bootstrap ? (
              <div className="np-actions">
                <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.adminBootstrap(), 'You can now change settings.')}>
                  Let me set this up
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="np-root" data-testid="neveroverpay-admin">
      <div className="np-wrap">
        <NeverOverpayNav mark="Settings" />
        {error ? <div className="np-error" style={{ marginBottom: 16 }}>{error}</div> : null}
        {notice ? <div className="np-notice" style={{ marginBottom: 16 }}>{notice}</div> : null}

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>AI</h3>
          <p className="np-muted">
            AI reads messy company emails, pulls quoted facts, explains why this might qualify, and drafts the request.
            It cannot open a recovery or mark money saved — those stay in rules and your bank statement.
            Uses the OpenAI key from this computer’s backend (.env OPENAI_API_KEY) or your App Builder key.
          </p>
          <label className="np-check">
            <input type="checkbox" checked={Boolean(form.ai_enabled)} onChange={(e) => setField('ai_enabled', e.target.checked)} />
            Use AI to read emails and draft the request
          </label>
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Do this for me</h3>
          <p className="np-muted">
            When this is on, we watch email, open recoveries you qualify for, send the request
            (after your standing OK), read the company’s reply, and count matching amounts as money back.
            We will not make a payment, change a plan, or close an account.
          </p>
          <label className="np-check">
            <input
              type="checkbox"
              checked={Boolean(form.autonomous_enabled)}
              onChange={(e) => {
                const on = e.target.checked;
                setForm((prev) => ({
                  ...prev,
                  autonomous_enabled: on,
                  autonomous_auto_approve: on ? true : prev.autonomous_auto_approve,
                  autonomous_send: on ? true : prev.autonomous_send,
                  autonomous_verify: on ? true : prev.autonomous_verify,
                  outbound_email_enabled: on ? true : prev.outbound_email_enabled,
                  gmail_sync_enabled: on ? true : prev.gmail_sync_enabled,
                }));
              }}
            />
            Enable “do this for me”
          </label>
          <label className="np-check">
            <input type="checkbox" checked={Boolean(form.autonomous_auto_approve)} onChange={(e) => setField('autonomous_auto_approve', e.target.checked)} />
            Approve requests under the dollar cap without asking each time
          </label>
          <label className="np-check">
            <input type="checkbox" checked={Boolean(form.autonomous_send)} onChange={(e) => setField('autonomous_send', e.target.checked)} />
            Send the request as soon as it is approved
          </label>
          <label className="np-check">
            <input type="checkbox" checked={Boolean(form.autonomous_verify)} onChange={(e) => setField('autonomous_verify', e.target.checked)} />
            Count it as money back when the company confirms the exact amount
          </label>
          <div className="np-form-grid">
            <label>Check email every (seconds)
              <input className="np-input" type="number" value={form.autonomous_interval_seconds} onChange={(e) => setField('autonomous_interval_seconds', e.target.value)} />
            </label>
            <label>Largest auto-send (cents, 50000 = $500)
              <input className="np-input" type="number" value={form.autonomous_max_cents} onChange={(e) => setField('autonomous_max_cents', e.target.value)} />
            </label>
          </div>
          <p className="np-muted" style={{ marginTop: 8 }}>
            Last check: emails {health?.autonomy?.ingested ?? '—'} · approved {health?.autonomy?.approved ?? '—'} ·
            sent {health?.autonomy?.sent ?? '—'} · replies {health?.autonomy?.replies ?? '—'} ·
            money counted {health?.autonomy?.verified ?? '—'}
            {health?.autonomy?.at ? ` · ${health.autonomy.at}` : ''}
          </p>
          <div className="np-actions">
            <button type="button" className="np-btn" disabled={busy} onClick={() => run(save, 'Settings saved.')}>
              Save settings
            </button>
            <button
              type="button"
              className="np-btn ghost"
              disabled={busy}
              onClick={() => run(async () => {
                await save();
                await neveroverpayApi.autonomyTick();
              }, 'Saved and ran a check now.')}
            >
              Save and check now
            </button>
          </div>
        </div>

        <div className="np-grid-3" style={{ marginBottom: 24 }}>
          {flags(setup).map(([label, ok]) => (
            <div className="np-card" key={label}>
              <div className="np-muted">{label}</div>
              <div className="np-pill" style={{ marginTop: 8 }}>{ok ? 'Ready' : 'Not ready'}</div>
            </div>
          ))}
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Who can change settings</h3>
          <p className="np-muted">Email addresses, separated by commas.</p>
          <input className="np-input" value={form.operator_emails} onChange={(e) => setField('operator_emails', e.target.value)} placeholder="ops@yourcompany.com" />
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>How we send email</h3>
          <p className="np-muted">This is the mailbox we send recovery requests from. Leave password blank to keep the one already saved.</p>
          <div className="np-form-grid">
            <label>Host<input className="np-input" value={form.smtp_host} onChange={(e) => setField('smtp_host', e.target.value)} placeholder="smtp.gmail.com" /></label>
            <label>Port<input className="np-input" type="number" value={form.smtp_port} onChange={(e) => setField('smtp_port', e.target.value)} /></label>
            <label>Username<input className="np-input" value={form.smtp_username} onChange={(e) => setField('smtp_username', e.target.value)} /></label>
            <label>Password<input className="np-input" type="password" value={form.smtp_password} onChange={(e) => setField('smtp_password', e.target.value)} placeholder={secretHints.smtp_password ? 'Saved · paste to replace' : ''} /></label>
            <label>From email<input className="np-input" value={form.smtp_from_email} onChange={(e) => setField('smtp_from_email', e.target.value)} /></label>
            <label>From name<input className="np-input" value={form.smtp_from_name} onChange={(e) => setField('smtp_from_name', e.target.value)} /></label>
          </div>
          <label className="np-check">
            <input type="checkbox" checked={Boolean(form.smtp_use_tls)} onChange={(e) => setField('smtp_use_tls', e.target.checked)} />
            Use TLS
          </label>
          <label className="np-check">
            <input type="checkbox" checked={Boolean(form.outbound_email_enabled)} onChange={(e) => setField('outbound_email_enabled', e.target.checked)} />
            Turn on sending recovery emails
          </label>
          <div className="np-actions">
            <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.testEmail(), 'Test email sent to your account.')}>
              Send test email
            </button>
          </div>
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Forward company emails here</h3>
          <p className="np-muted">
            People can forward refund and renewal emails to their BilledTwice address.
            Your email provider posts to <code>/api/neveroverpay/inbound/email</code> with the secret below.
          </p>
          {access.forwarding_address ? (
            <p className="np-pre">{access.forwarding_address}</p>
          ) : (
            <p className="np-muted">Add a forwarding domain so we can give each person an address.</p>
          )}
          <div className="np-form-grid">
            <label>Forwarding domain<input className="np-input" value={form.inbound_domain} onChange={(e) => setField('inbound_domain', e.target.value)} placeholder="inbound.billedtwice.ai" /></label>
            <label>Forwarding secret<input className="np-input" type="password" value={form.inbound_secret} onChange={(e) => setField('inbound_secret', e.target.value)} placeholder={secretHints.inbound_secret ? 'Saved · paste to replace' : ''} /></label>
          </div>
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Connect Gmail</h3>
          <p className="np-muted">
            Same Google OAuth client as Snagcart login. Enable Gmail API, then add this exact
            Authorized redirect URI on the OAuth client:
            <code> http://localhost:3000/api/auth/google/callback</code>
            (sign-in and Gmail connect share it).
          </p>
          <div className="np-form-grid">
            <label>Client ID<input className="np-input" value={form.google_client_id} onChange={(e) => setField('google_client_id', e.target.value)} /></label>
            <label>Client secret<input className="np-input" type="password" value={form.google_client_secret} onChange={(e) => setField('google_client_secret', e.target.value)} placeholder={secretHints.google_client_secret ? 'Saved · paste to replace' : ''} /></label>
            <label>Redirect URI<input className="np-input" value={form.google_redirect_uri} onChange={(e) => setField('google_redirect_uri', e.target.value)} /></label>
            <label>Public app URL<input className="np-input" value={form.public_app_url} onChange={(e) => setField('public_app_url', e.target.value)} /></label>
          </div>
          <label className="np-check">
            <input type="checkbox" checked={Boolean(form.gmail_sync_enabled)} onChange={(e) => setField('gmail_sync_enabled', e.target.checked)} />
            Let people connect Gmail so we can check recovery emails
          </label>
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Bank match (Plaid)</h3>
          <p className="np-muted">
            From the Plaid Dashboard, copy the sandbox client ID and secret. Allowed products are
            Transactions only — BilledTwice does not move money. Link requests every Plaid country
            (US, Canada, UK, and Europe). Production only shows countries your Plaid account is
            approved for. Register redirect URI
            <code> http://localhost:3000/plaid/oauth</code> for OAuth banks. After you save,
            people can click Connect Bank Securely during setup or on Settings.
          </p>
          <div className="np-form-grid">
            <label>Client ID<input className="np-input" value={form.plaid_client_id} onChange={(e) => setField('plaid_client_id', e.target.value)} placeholder="Plaid client_id" /></label>
            <label>Secret<input className="np-input" type="password" value={form.plaid_secret} onChange={(e) => setField('plaid_secret', e.target.value)} placeholder={secretHints.plaid_secret ? 'Saved · paste to replace' : ''} /></label>
            <label>OAuth redirect URI<input className="np-input" value={form.plaid_redirect_uri || ''} onChange={(e) => setField('plaid_redirect_uri', e.target.value)} placeholder="http://localhost:3000/plaid/oauth" /></label>
            <label>Webhook URL<input className="np-input" value={form.plaid_webhook_url || ''} onChange={(e) => setField('plaid_webhook_url', e.target.value)} placeholder="https://your-api/api/neveroverpay/plaid/webhook" /></label>
            <label>Country codes<input className="np-input" value={form.plaid_country_codes || ''} onChange={(e) => setField('plaid_country_codes', e.target.value)} placeholder="Blank = all Plaid countries (US, CA, GB, FR, …)" /></label>
          </div>
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>How long we keep files</h3>
          <label>Days to keep uploaded emails
            <input className="np-input" type="number" value={form.retention_days} onChange={(e) => setField('retention_days', e.target.value)} />
          </label>
        </div>

        <div className="np-actions" style={{ marginBottom: 32 }}>
          <button type="button" className="np-btn" disabled={busy} onClick={() => run(save, 'Platform settings saved.')}>
            Save settings
          </button>
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>At a glance</h3>
          <p className="np-muted">
            Recoveries {health?.counts?.cases ?? '—'} · Emails {health?.counts?.documents ?? '—'} ·
            Requests sent {health?.counts?.executions ?? '—'} · Gmail connected {health?.counts?.gmail_accounts ?? '—'}
          </p>
          {health?.infra ? (
            <p className="np-muted" style={{ marginTop: 8 }}>
              KMS {health.infra.kms ? 'on' : 'off'} · S3 {health.infra.s3 ? 'on' : 'off'} ·
              Postgres {health.infra.postgres ? 'on' : 'off'} · Temporal {health.infra.temporal ? 'on' : 'off'}
            </p>
          ) : null}
          <div className="np-actions">
            <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.deadlineSweep(), 'Checked overdue renewals.')}>
              Check overdue renewals
            </button>
          </div>
        </div>

        <div className="np-card">
          <h3>Where we email each company</h3>
          <p className="np-muted">Addresses are taken from the From / Reply-To on real mail. Add a company here only if you need a different inbox.</p>
          {playbooks.length === 0 ? (
            <p className="np-muted">None yet. After a real recovery is opened, the company address from that email shows up here.</p>
          ) : null}
          {playbooks.map((row, idx) => (
            <div className="np-form-grid" key={`${row.merchant_slug}-${row.workflow}`} style={{ marginTop: 16 }}>
              <label>Company
                <input className="np-input" value={row.merchant || ''} onChange={(e) => {
                  const next = [...playbooks];
                  next[idx] = { ...row, merchant: e.target.value };
                  setPlaybooks(next);
                }} />
              </label>
              <label>Email to
                <input className="np-input" value={row.to || ''} onChange={(e) => {
                  const next = [...playbooks];
                  next[idx] = { ...row, to: e.target.value };
                  setPlaybooks(next);
                }} />
              </label>
              <div className="np-muted" style={{ gridColumn: '1 / -1' }}>{workflowLabel(row.workflow)}</div>
              <button
                type="button"
                className="np-btn ghost"
                disabled={busy}
                onClick={() => run(() => neveroverpayApi.savePlaybook(row), `Saved ${row.merchant}`)}
              >
                Save company
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
